import type { FC, ReactNode } from 'react'

import { Navigation, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { directionsUrl, whatsappUrl } from '@/lib/business'
import { WhatsAppIcon } from '@/components/generals/icons'
import { contact } from '@/constants/site'

export type ButtonVariant = 'gold' | 'plum' | 'whatsapp' | 'outline-light' | 'outline-dark'
export type ButtonSize = 'md' | 'sm'

const baseClass = 'inline-flex items-center justify-center gap-2 rounded-full font-semibold transition-[filter,background-color,border-color,transform] active:scale-[0.98] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'

const sizeClasses: Record<ButtonSize, string> = {
    md: 'h-12 px-6 text-sm',
    sm: 'h-10 px-4 text-xs'
}

const variantClasses: Record<ButtonVariant, string> = {
    'gold': 'gold-gradient text-plum-deep shadow-lg shadow-gold/25 hover:brightness-110',
    'plum': 'bg-plum text-ivory shadow-lg shadow-plum/20 hover:brightness-125',
    'whatsapp': 'bg-[#1fae53] text-white shadow-lg shadow-black/10 hover:brightness-110',
    'outline-light': 'border border-gold/60 text-gold-light hover:border-gold hover:bg-white/5',
    'outline-dark': 'border border-plum/20 bg-white text-plum hover:border-gold hover:bg-accent/40'
}

export const buttonClass = (variant: ButtonVariant, size: ButtonSize = 'md', className?: string): string => {
    return cn(baseClass, sizeClasses[size], variantClasses[variant], className)
}

interface ContactButtonProps {
    label?: string
    variant?: ButtonVariant
    size?: ButtonSize
    className?: string
}

interface WhatsAppButtonProps extends ContactButtonProps {
    message?: string
}

export const WhatsAppButton: FC<WhatsAppButtonProps> = ({
    message,
    label = 'Book on WhatsApp',
    variant = 'gold',
    size = 'md',
    className
}): ReactNode => {
    return (
        <a
            href={whatsappUrl(message)}
            target='_blank'
            rel='noopener noreferrer'
            className={buttonClass(variant, size, className)}
        >
            <WhatsAppIcon className={size === 'sm' ? 'size-4' : 'size-5'} />
            {label}
        </a>
    )
}

export const CallButton: FC<ContactButtonProps> = ({
    label = `Call ${contact.phoneDisplay}`,
    variant = 'outline-light',
    size = 'md',
    className
}): ReactNode => {
    return (
        <a href={contact.phoneHref} className={buttonClass(variant, size, className)}>
            <Phone className='size-4' aria-hidden='true' />
            {label}
        </a>
    )
}

export const DirectionsButton: FC<ContactButtonProps> = ({
    label = 'Get directions',
    variant = 'outline-dark',
    size = 'md',
    className
}): ReactNode => {
    return (
        <a
            href={directionsUrl()}
            target='_blank'
            rel='noopener noreferrer'
            className={buttonClass(variant, size, className)}
        >
            <Navigation className='size-4' aria-hidden='true' />
            {label}
        </a>
    )
}
